// Spin Fuel
import { useEffect, useMemo, useState, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { motion } from 'framer-motion';
import { createPortal } from 'react-dom';
import { useGlobalContext } from '../../data/GlobalContext';
import { spinFuelMilestones } from './FortuneRushTermsAndConditions';
import { promoURL } from '../../../App';

const ITEMS_PER_PAGE = 4;
const TEST_MODE = false;
const TEST_TURNOVER = 15000;

const SPIN_FUEL_REWARDS = [
    {
        id: 1,
        minTurnover: 15000,
        maxTurnover: 74999,
        milestone: 'M1',
        credits: 50,
    },
    {
        id: 2,
        minTurnover: 75000,
        maxTurnover: 349999,
        milestone: 'M2',
        credits: 80,
    },
    {
        id: 3,
        minTurnover: 350000,
        maxTurnover: 1499999,
        milestone: 'M3',
        credits: 150,
    },
    {
        id: 4,
        minTurnover: 1500000,
        maxTurnover: null,
        milestone: 'M4',
        credits: 1000,
    },
];

export default function RewardNotifications({ onToast }) {
    const {
        username,
        isLoggedIn,
        jiliFortuneRushInfo,
        jiliFortuneRushLoading,
        fetchJiliFortuneRushInfo,
    } = useGlobalContext();

    const [currentPage, setCurrentPage] = useState(1);
    const [showInfo, setShowInfo] = useState(false);
    const [position, setPosition] = useState({});
    const [showTable, setShowTable] = useState(false);
    const [locallyClaimedMilestones, setLocallyClaimedMilestones] = useState(
        []
    );
    const infoRef = useRef(null);

    /*
	|--------------------------------------------------------------------------
	| SPIN FUEL API DATA
	|--------------------------------------------------------------------------
	*/

    const apiSpinFuelTurnover = Number(
        jiliFortuneRushInfo?.spinFuelTurnover ??
            jiliFortuneRushInfo?.spinFuel?.turnover ??
            0
    );

    const spinFuelTurnover = TEST_MODE ? TEST_TURNOVER : apiSpinFuelTurnover;

    const currentMilestone =
        jiliFortuneRushInfo?.currentMilestone ??
        jiliFortuneRushInfo?.spinFuel?.currentMilestone ??
        null;

    const claimableRewards =
        jiliFortuneRushInfo?.claimableRewards ??
        (currentMilestone
            ? jiliFortuneRushInfo?.[currentMilestone]?.claimableRewards
            : null) ??
        jiliFortuneRushInfo?.spinFuel?.claimableRewards ??
        null;

    const isRewardClaimed =
        String(claimableRewards).trim().toLowerCase() === 'claimed';

    const nextMilestone =
        jiliFortuneRushInfo?.nextMilestone ??
        jiliFortuneRushInfo?.spinFuel?.nextMilestone ??
        null;

    const nextTarget = Number(
        jiliFortuneRushInfo?.nextTarget ??
            jiliFortuneRushInfo?.spinFuel?.nextTarget ??
            0
    );

    const claimableMilestones = useMemo(() => {
        if (!claimableRewards) return [];

        if (Array.isArray(claimableRewards)) {
            return claimableRewards
                .map((item) => {
                    if (typeof item === 'string') {
                        return item;
                    }

                    return (
                        item?.milestone ??
                        item?.currentMilestone ??
                        item?.milestoneCode ??
                        null
                    );
                })
                .filter(Boolean);
        }

        if (typeof claimableRewards === 'string') {
            return claimableRewards
                .split(',')
                .map((item) => item.trim())
                .filter(Boolean);
        }

        return [];
    }, [claimableRewards]);

    /*
	|--------------------------------------------------------------------------
	| REFRESH SPIN FUEL
	|--------------------------------------------------------------------------
	*/

    useEffect(() => {
        if (!isLoggedIn || !username) return;

        fetchJiliFortuneRushInfo(username);
    }, [isLoggedIn, username, fetchJiliFortuneRushInfo]);

    /*
	|--------------------------------------------------------------------------
	| BUILD REWARDS USING API TURNOVER
	|--------------------------------------------------------------------------
	*/

    const rewards = useMemo(() => {
        if (!jiliFortuneRushInfo) return [];

        const rewardEntries = [];

        Object.entries(jiliFortuneRushInfo).forEach(
            ([key, milestoneApiData]) => {
                // Examples:
                // 20260924-M1
                // 20261005-M3
                const match = key.match(/^(\d{8})-(M[1-4])$/i);

                if (!match) return;

                const [, rewardDate, milestoneRaw] = match;
                const milestone = milestoneRaw.toUpperCase();

                if (!milestoneApiData || typeof milestoneApiData !== 'object') {
                    return;
                }

                const rewardConfig = SPIN_FUEL_REWARDS.find(
                    (reward) => reward.milestone === milestone
                );

                if (!rewardConfig) return;

                const milestoneStatus = String(
                    milestoneApiData?.claimableRewards ?? ''
                )
                    .trim()
                    .toLowerCase();

                const rewardClaimKey = `${rewardDate}-${milestone}`;

                const locallyClaimed =
                    locallyClaimedMilestones.includes(rewardClaimKey);

                const apiClaimed = milestoneStatus === 'claimed';
                const apiClaimable = milestoneStatus === 'claimable';

                const claimed = apiClaimed || locallyClaimed;

                /*
                 * IMPORTANT:
                 *
                 * Do NOT use today's spinFuelTurnover to determine whether
                 * a historical reward is claimable.
                 *
                 * If the API says "Claimable", then it is claimable.
                 */
                const claimable = apiClaimable && !claimed;

                rewardEntries.push({
                    ...rewardConfig,

                    id: rewardClaimKey,

                    rewardDate,
                    milestone,

                    claimed,
                    claimable,

                    claimStatus: milestoneStatus || null,

                    /*
                     * This reward was already earned historically,
                     * so show the milestone as completed.
                     */
                    currentTurnover:
                        apiClaimable || apiClaimed
                            ? rewardConfig.minTurnover
                            : 0,
                });
            }
        );

        /*
         * Newest rewards first.
         *
         * Same date:
         * M1 -> M2 -> M3 -> M4
         */
        return rewardEntries.sort((a, b) => {
            // 1. Latest date first
            const dateDiff = Number(b.rewardDate) - Number(a.rewardDate);

            if (dateDiff !== 0) {
                return dateDiff;
            }

            // 2. Same date -> M1, M2, M3, M4
            const aMilestone = Number(String(a.milestone).replace(/\D/g, ''));

            const bMilestone = Number(String(b.milestone).replace(/\D/g, ''));

            return aMilestone - bMilestone;
        });
    }, [jiliFortuneRushInfo, locallyClaimedMilestones]);
    /*
|--------------------------------------------------------------------------
| ELIGIBLE + NEXT MILESTONE
|--------------------------------------------------------------------------
*/

    const eligibleRewards = useMemo(() => {
        return rewards.filter(
            (reward) =>
                Number(reward.currentTurnover) >= Number(reward.minTurnover)
        );
    }, [rewards]);

    const nextMilestoneReward = useMemo(() => {
        if (!nextMilestone || !nextTarget) return null;

        const rewardConfig = SPIN_FUEL_REWARDS.find(
            (reward) =>
                String(reward.milestone).toLowerCase() ===
                String(nextMilestone).toLowerCase()
        );

        if (!rewardConfig) return null;

        return {
            ...rewardConfig,

            // Use actual current turnover for the progress
            currentTurnover: spinFuelTurnover,

            // API is the source of truth for the next target
            minTurnover: nextTarget,

            claimed: false,
            claimable: false,
            isNextMilestone: true,
        };
    }, [nextMilestone, nextTarget, spinFuelTurnover]);

    const todayRewardDate = useMemo(() => {
        const parts = new Intl.DateTimeFormat('en-CA', {
            timeZone: 'Asia/Manila',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        }).formatToParts(new Date());

        const year = parts.find((part) => part.type === 'year')?.value;
        const month = parts.find((part) => part.type === 'month')?.value;
        const day = parts.find((part) => part.type === 'day')?.value;

        return `${year}${month}${day}`;
    }, []);

    const displayedRewards = useMemo(() => {
        /*
         * 1. Historical/current rewards from API
         */
        const result = rewards.filter((reward) => {
            const isToday = String(reward.rewardDate) === todayRewardDate;

            /*
             * TODAY:
             * Keep Claimable + Claimed.
             *
             * Historical IN PROGRESS records normally do not exist
             * because nextMilestoneReward is generated separately below.
             */
            if (isToday) {
                return true;
            }

            /*
             * PREVIOUS DAYS:
             * Only keep rewards still waiting to be claimed.
             */
            return reward.claimable;
        });

        /*
         * 2. Keep current IN PROGRESS milestone
         */
        if (nextMilestoneReward) {
            const existingIndex = result.findIndex(
                (reward) =>
                    String(reward.milestone).toUpperCase() ===
                        String(nextMilestoneReward.milestone).toUpperCase() &&
                    String(reward.rewardDate) === todayRewardDate
            );

            const currentProgressReward = {
                ...nextMilestoneReward,

                /*
                 * Give today's date so sorting works correctly.
                 */
                rewardDate: todayRewardDate,

                /*
                 * Unique ID
                 */
                id: `${todayRewardDate}-${nextMilestoneReward.milestone}-progress`,
            };

            if (existingIndex >= 0) {
                /*
                 * Replace today's same milestone with
                 * its current progress version.
                 */
                result[existingIndex] = currentProgressReward;
            } else {
                result.push(currentProgressReward);
            }
        }

        /*
         * 3. Latest date first
         * Same date: M1 -> M2 -> M3 -> M4
         */
        return result.sort((a, b) => {
            const dateDiff =
                Number(b.rewardDate || 0) - Number(a.rewardDate || 0);

            if (dateDiff !== 0) {
                return dateDiff;
            }

            const aMilestone = Number(String(a.milestone).replace(/\D/g, ''));

            const bMilestone = Number(String(b.milestone).replace(/\D/g, ''));

            return aMilestone - bMilestone;
        });
    }, [rewards, nextMilestoneReward, todayRewardDate]);

    /*
	|--------------------------------------------------------------------------
	| UNCLAIMED
	|--------------------------------------------------------------------------
	*/

    const unclaimedRewards = useMemo(() => {
        return rewards.filter((reward) => reward.claimable);
    }, [rewards]);

    /*
	|--------------------------------------------------------------------------
	| CLAIMED
	|--------------------------------------------------------------------------
	*/

    const claimedEligibleRewards = useMemo(() => {
        return eligibleRewards.filter((reward) => reward.claimed);
    }, [eligibleRewards]);

    /*
	|--------------------------------------------------------------------------
	| TOTAL UNCLAIMED CREDITS
	|--------------------------------------------------------------------------
	*/

    const totalUnclaimed = useMemo(() => {
        return unclaimedRewards.reduce(
            (total, reward) => total + Number(reward.credits || 0),
            0
        );
    }, [unclaimedRewards]);

    /*
	|--------------------------------------------------------------------------
	| PAGINATION
	|--------------------------------------------------------------------------
	*/

    const totalPages = Math.ceil(displayedRewards.length / ITEMS_PER_PAGE);

    const paginatedRewards = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;

        return displayedRewards.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [displayedRewards, currentPage]);

    useEffect(() => {
        if (totalPages === 0) {
            setCurrentPage(1);
            return;
        }

        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]);

    const goToPage = (page) => {
        if (page < 1 || page > totalPages) return;

        setCurrentPage(page);
    };

    /*
	|--------------------------------------------------------------------------
	| INFO POPUP
	|--------------------------------------------------------------------------
	*/

    const handleMouseEnter = () => {
        if (!infoRef.current) return;

        const rect = infoRef.current.getBoundingClientRect();

        setPosition({
            top: rect.bottom + window.scrollY + 12,

            left: rect.right + window.scrollX - 430,
        });

        setShowInfo(true);
    };

    const handleMouseLeave = () => {
        setShowInfo(false);
    };

    const handleCloseTable = () => {
        setShowTable(false);
    };

    if (!TEST_MODE && jiliFortuneRushLoading && !jiliFortuneRushInfo) {
        return (
            <div className="reward-page">
                <div className="reward-modal">
                    <div className="reward-empty-state">
                        Loading Spin Fuel...
                    </div>
                </div>
            </div>
        );
    }

    const handleShowTable = () => {
        setShowTable(true);
    };

    const tabVariants = {
        initial: {
            opacity: 0,
            y: 15,
        },

        animate: {
            opacity: 1,
            y: 0,
        },

        exit: {
            opacity: 0,
            y: -15,
        },
    };

    return (
        <>
            <motion.div
                initial={{
                    opacity: 0,
                    scale: 0.97,
                }}
                animate={{
                    opacity: 1,
                    scale: 1,
                }}
                transition={{
                    delay: 0.1,
                    duration: 0.25,
                }}
                className="reward-page"
            >
                <div className="reward-modal">
                    {/* HEADING */}
                    <div className="reward-list-heading">
                        <div>
                            <span>REWARD HISTORY</span>

                            <strong>
                                {eligibleRewards.length}{' '}
                                {eligibleRewards.length === 1
                                    ? 'Reward'
                                    : 'Rewards'}
                            </strong>
                        </div>

                        {unclaimedRewards.length > 0 && (
                            <span className="pending-rewards">
                                {unclaimedRewards.length} waiting to claim
                            </span>
                        )}
                    </div>
                    {/* REWARDS */}
                    {!showTable && (
                        <div className="rewards-list">
                            {paginatedRewards.length > 0 ? (
                                paginatedRewards.map((reward) => (
                                    <RewardCard
                                        key={reward.id}
                                        reward={reward}
                                        onToast={onToast}
                                        onClaimed={(rewardKey) => {
                                            setLocallyClaimedMilestones(
                                                (prev) => [
                                                    ...new Set([
                                                        ...prev,
                                                        rewardKey,
                                                    ]),
                                                ]
                                            );
                                        }}
                                    />
                                ))
                            ) : (
                                <div className="reward-empty-state">
                                    <img
                                        src="/images/game/jili-rush/JILI-RUSH-NO-DATA.webp"
                                        alt=""
                                    />
                                    No rewards unlocked yet.
                                </div>
                            )}
                        </div>
                    )}
                    {showTable && (
                        <motion.div
                            variants={tabVariants}
                            initial="initial"
                            animate="animate"
                            exit="exit"
                            transition={{
                                duration: 0.2,
                            }}
                        >
                            <div className="fr-tnc-table-wrapper fr-stats-panel">
                                <table className="fr-tnc-table">
                                    <thead>
                                        <tr>
                                            <th>Milestone</th>
                                            <th>Turnover Target</th>
                                            <th>Free Credits Reward</th>
                                            <th>Rollover Requirement</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {spinFuelMilestones.map(
                                            (item, index) => (
                                                <tr key={item.milestone}>
                                                    <td>
                                                        <strong>
                                                            {item.milestone}
                                                        </strong>
                                                    </td>

                                                    <td>{item.turnover}</td>

                                                    <td>{item.reward}</td>

                                                    {index === 0 && (
                                                        <td
                                                            rowSpan={
                                                                spinFuelMilestones.length
                                                            }
                                                        >
                                                            <strong>
                                                                1x Rollover
                                                            </strong>{' '}
                                                            requirement
                                                        </td>
                                                    )}
                                                </tr>
                                            )
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </motion.div>
                    )}

                    {/* INFO */}
                    <div className="jili-infos">
                        <img
                            ref={infoRef}
                            src="/images/game/jili-rush/JILI_RUSH_INFO.webp"
                            alt="info"
                            onMouseEnter={handleMouseEnter}
                            onMouseLeave={handleMouseLeave}
                        />{' '}
                        {!showTable && totalPages > 1 && (
                            <div className="reward-pagination">
                                <button
                                    type="button"
                                    className="pagination-arrow"
                                    onClick={() => goToPage(currentPage - 1)}
                                    disabled={currentPage === 1}
                                >
                                    ‹
                                </button>

                                <div className="pagination-info">
                                    <span>
                                        Page {currentPage} of {totalPages}
                                    </span>

                                    <small>View other rewards</small>
                                </div>

                                <button
                                    type="button"
                                    className="pagination-arrow"
                                    onClick={() => goToPage(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                >
                                    ›
                                </button>
                            </div>
                        )}
                        {!showTable && (
                            <div className="milestone-panel-table">
                                <button onClick={handleShowTable}>View</button>
                            </div>
                        )}
                        {showTable && (
                            <div className="stats-panel-table">
                                <button onClick={handleCloseTable}>
                                    <FontAwesomeIcon
                                        icon={faArrowLeft}
                                        className="fa-deposit-icon"
                                    />{' '}
                                    Back
                                </button>
                            </div>
                        )}
                    </div>
                    {showInfo &&
                        createPortal(
                            <div
                                className="jili-info-popup jili-imp-notes"
                                style={{
                                    position: 'absolute',

                                    top: position.top,

                                    left: position.left,

                                    zIndex: 999999,
                                }}
                                onMouseEnter={() => setShowInfo(true)}
                            >
                                <div className="jili-info-header">
                                    <img
                                        src="/images/game/jili-rush/JILI-NOTE.webp"
                                        alt=""
                                    />
                                    IMPORTANT NOTES
                                </div>

                                <div className="jili-info-content">
                                    <div>
                                        <div className="jili-info-item">
                                            <p>
                                                Unclaimed Free Credits expire
                                                when the promotion ends.
                                            </p>
                                        </div>

                                        <div className="jili-info-item">
                                            <p>
                                                Claimed credits carry a{' '}
                                                <strong>
                                                    1x rollover requirement.
                                                </strong>
                                            </p>
                                        </div>
                                    </div>

                                    <div className="jili-info-item--candy">
                                        <img
                                            src="/images/game/jili-rush/JILI-CANDY.webp"
                                            alt=""
                                        />
                                    </div>
                                </div>
                            </div>,

                            document.body
                        )}
                    {/* PAGINATION */}
                </div>
            </motion.div>
        </>
    );
}

function RewardCard({ reward, onClaimed, onToast }) {
    const { username, fetchJiliFortuneRushInfo } = useGlobalContext();

    const [isClaiming, setIsClaiming] = useState(false);

    const handleClaimReward = async () => {
        if (
            reward.claimed ||
            !reward.claimable ||
            isClaiming ||
            !reward.rewardDate
        ) {
            return;
        }

        setIsClaiming(true);

        try {
            const params = new URLSearchParams({
                cmd: 'claimJiliFortuneRushReward',
                u: username,
                milestone: reward.milestone,
                // Format: yyyymmdd
                day: reward.rewardDate,
            });

            console.log('Claiming reward:', {
                username,
                milestone: reward.milestone,
                day: reward.rewardDate,
            });

            const response = await fetch(`${promoURL}?${params.toString()}`);

            if (!response.ok) {
                throw new Error(`HTTP error: ${response.status}`);
            }

            const data = await response.json();

            console.log('Claim response:', data);
            console.log('Claimed reward:', reward.rewardDate, reward.milestone);

            if (data.s) {
                /*
                 * Important:
                 * Since M1 can now exist on multiple days,
                 * identify the claimed reward using:
                 *
                 * YYYYMMDD-MILESTONE
                 *
                 * Example:
                 * 20261005-M3
                 */
                onClaimed?.(`${reward.rewardDate}-${reward.milestone}`);
                onToast?.(
                    `₱${Number(reward.credits || 0).toLocaleString()} Free Credits claimed successfully!`,
                    'success'
                );

                // Refresh latest API state
                await fetchJiliFortuneRushInfo(username);
            } else {
                onToast?.(
                    data?.m || data?.message || 'Unable to claim reward.',
                    'error'
                );
            }
        } catch (error) {
            console.error('Failed to claim reward:', error);
            onToast?.('Failed to claim reward. Please try again.', 'error');
        } finally {
            setIsClaiming(false);
        }
    };
    const formatRewardDate = (dateString) => {
        if (!dateString || dateString.length !== 8) return '-';

        const year = dateString.slice(0, 4);
        const month = dateString.slice(4, 6);
        const day = dateString.slice(6, 8);

        const date = new Date(`${year}-${month}-${day}T00:00:00`);

        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        });
    };
    return (
        <div
            className={`reward-card ${
                reward.claimed ? 'reward-card-claimed' : 'reward-card-active'
            }`}
        >
            {/* MILESTONE */}

            <div className="reward-date">
                <span className="reward-field-label reward-field-label--date">
                    MILESTONE
                </span>

                <strong>
                    {reward.milestone} - {formatRewardDate(reward.rewardDate)}
                </strong>
            </div>

            {/* Milestone Mobile */}
            <div className="reward-date-tag">
                <strong>{reward.milestone}</strong>
            </div>

            <div className="reward-divider" />

            {/* TURNOVER */}

            <div className="turnover-section">
                <div className="mobile-milestone-date">
                    {formatRewardDate(reward.rewardDate)}
                </div>
                <span className="reward-field-label">
                    {reward.isNextMilestone ? 'TURNOVER PROGRESS' : 'TURNOVER'}
                </span>
                <strong>
                    {Number(reward.currentTurnover || 0).toLocaleString()} /{' '}
                    {Number(reward.minTurnover || 0).toLocaleString()}
                </strong>

                {!reward.claimed &&
                    Number(reward.currentTurnover || 0) <
                        Number(reward.minTurnover || 0) && (
                        <p className="turnover-needed">
                            <span>
                                {(
                                    Number(reward.minTurnover || 0) -
                                    Number(reward.currentTurnover || 0)
                                ).toLocaleString()}{' '}
                            </span>
                            more to unlock
                        </p>
                    )}
            </div>

            {/* REWARD */}

            <div className="credit-section">
                <div
                    className={`reward-chest ${
                        reward.claimed ? 'reward-chest-disabled' : ''
                    }`}
                >
                    <img src="/images/game/jili-rush/chest-01.webp" alt="" />
                </div>

                <div className="credit-text">
                    <span className="reward-field-label">FREE CREDITS</span>

                    <strong>₱{reward.credits.toLocaleString()}</strong>
                </div>
            </div>

            {/* ACTION */}

            <div className="button-section">
                {reward.isNextMilestone ? (
                    <div className="next-milestone-button">IN PROGRESS</div>
                ) : reward.claimed ? (
                    <img
                        src="/images/game/jili-rush/JILI-RUSH-CLAIMED.webp"
                        alt="Claimed"
                        className="btn-claimed"
                    />
                ) : reward.claimable ? (
                    <img
                        onClick={handleClaimReward}
                        src="/images/game/jili-rush/JILI-RUSH-CLAIM_NOW.webp"
                        alt="Claim now"
                        role="button"
                        className="btn-claim-now"
                        style={{
                            cursor: 'pointer',
                        }}
                    />
                ) : (
                    <div className="next-milestone-button">IN PROGRESS</div>
                )}
            </div>
        </div>
    );
}
